import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useLanguage } from "../App";
import {
  Sparkles,
  Target,
  TrendingUp,
  Wallet,
  Loader2,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { Button } from "./ui/button";

const WhatsAppUpsellDashboard = () => {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/wa/upsell/analytics`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error("WA upsell analytics error:", err);
      const msg = language === "it"
        ? "Impossibile caricare le analytics upsell. Riprova."
        : "Unable to load upsell analytics. Please retry.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const formatCurrency = (n) =>
    (Number(n) || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

  const formatDate = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="upsell-loading">
        <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-card border border-red-200 dark:border-red-900 rounded-2xl p-8 text-center" data-testid="upsell-error">
        <p className="text-sm text-red-600 dark:text-red-400 mb-3">
          {error || (language === "it" ? "Dati non disponibili" : "No data available")}
        </p>
        <Button variant="outline" size="sm" onClick={fetchData} className="rounded-full" data-testid="upsell-retry-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" />
          {language === "it" ? "Riprova" : "Retry"}
        </Button>
      </div>
    );
  }

  const hasData = data.total_impressions > 0;

  return (
    <div className="space-y-6" data-testid="wa-upsell-dashboard">
      {/* Header + refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-pink-500" />
          <h2 className="text-lg font-semibold text-foreground">
            {language === "it" ? "Performance Upsell WhatsApp" : "WhatsApp Upsell Performance"}
          </h2>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="rounded-full"
          data-testid="upsell-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          {language === "it" ? "Aggiorna" : "Refresh"}
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="upsell-kpi-cards">
        <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl p-5 text-white">
          <Target className="w-7 h-7 opacity-80 mb-2" />
          <p className="text-pink-100 text-xs">
            {language === "it" ? "Impression Totali" : "Total Impressions"}
          </p>
          <p className="text-2xl font-bold mt-1" data-testid="upsell-impressions">
            {data.total_impressions}
          </p>
          <p className="text-xs text-pink-100 mt-2">
            {data.month_impressions} {language === "it" ? "ultimi 30gg" : "last 30d"}
          </p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white">
          <Sparkles className="w-7 h-7 opacity-80 mb-2" />
          <p className="text-emerald-100 text-xs">
            {language === "it" ? "Conversioni" : "Conversions"}
          </p>
          <p className="text-2xl font-bold mt-1" data-testid="upsell-conversions">
            {data.total_conversions}
          </p>
          <p className="text-xs text-emerald-100 mt-2">
            {data.month_conversions} {language === "it" ? "ultimi 30gg" : "last 30d"}
          </p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-5 text-white">
          <TrendingUp className="w-7 h-7 opacity-80 mb-2" />
          <p className="text-amber-100 text-xs">
            {language === "it" ? "Tasso Conversione" : "Conversion Rate"}
          </p>
          <p className="text-2xl font-bold mt-1" data-testid="upsell-rate">
            {data.conversion_rate}%
          </p>
        </div>
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-5 text-white">
          <Wallet className="w-7 h-7 opacity-80 mb-2" />
          <p className="text-violet-100 text-xs">
            {language === "it" ? "Ricavo Extra" : "Extra Revenue"}
          </p>
          <p className="text-2xl font-bold mt-1" data-testid="upsell-revenue">
            {formatCurrency(data.total_revenue)}
          </p>
        </div>
      </div>

      {!hasData ? (
        <div className="bg-card border border-border rounded-2xl p-12 text-center" data-testid="upsell-empty">
          <Sparkles className="w-14 h-14 mx-auto text-pink-400 opacity-50 mb-3" />
          <h3 className="text-lg font-semibold text-foreground mb-1">
            {language === "it" ? "Nessun upsell tracciato ancora" : "No upsells tracked yet"}
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {language === "it"
              ? "Dopo ogni prenotazione su WhatsApp il bot suggerisce servizi correlati. Qui vedrai quanti clienti hanno poi prenotato un servizio suggerito."
              : "After each WhatsApp booking the bot suggests related services. Here you'll see how many clients booked a suggested one."}
          </p>
        </div>
      ) : (
        <>
          {/* Top Sources */}
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="upsell-top-sources">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Target className="w-4 h-4 text-pink-500" />
              {language === "it" ? "Servizi che generano più upsell" : "Top upsell triggers"}
            </h3>
            <div className="space-y-2">
              {data.top_sources.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                  data-testid={`upsell-source-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-medium text-foreground text-sm">{s.service_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.impressions} {language === "it" ? "impression" : "impressions"} • {s.conversions} {language === "it" ? "conversioni" : "conversions"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                      {s.conversion_rate}%
                    </p>
                    <p className="text-xs text-muted-foreground">{formatCurrency(s.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Converted */}
          {data.top_converted.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5" data-testid="upsell-top-converted">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-500" />
                {language === "it" ? "Servizi più acquistati via upsell" : "Top purchased via upsell"}
              </h3>
              <div className="space-y-2">
                {data.top_converted.map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    data-testid={`upsell-converted-${i}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <p className="font-medium text-foreground text-sm">{c.service_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-foreground">×{c.count}</p>
                      <p className="text-xs text-muted-foreground">{formatCurrency(c.revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Events */}
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="upsell-recent-events">
            <h3 className="text-sm font-semibold text-foreground mb-4">
              {language === "it" ? "Attività recente" : "Recent activity"}
            </h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {data.recent_events.map((ev, i) => (
                <div
                  key={ev.event_id || i}
                  className={`p-3 rounded-lg border text-sm ${
                    ev.converted
                      ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/20"
                      : "border-border bg-muted/30"
                  }`}
                  data-testid={`upsell-event-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-foreground font-medium">{ev.source_service_name}</span>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <span className="text-muted-foreground text-xs">
                        {(ev.suggestions || []).join(", ")}
                      </span>
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        ev.converted
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {ev.converted
                        ? (language === "it" ? "Convertito" : "Converted")
                        : (language === "it" ? "In attesa" : "Pending")}
                    </span>
                  </div>
                  {ev.converted && (
                    <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">
                      → {ev.converted_service_name} ({formatCurrency(ev.converted_revenue)})
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">{formatDate(ev.shown_at)}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WhatsAppUpsellDashboard;
